import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { FerieResource } from "types/centre.type";
import { FactureResource } from "types/facturation.types";
import { FilterProps, ListPaginated } from "types/list.type";
import {
  DesserteItem,
  DesserteResource,
  RecuResource,
  TourneeResource,
  VehiculeResource,
} from "types/transport.types";

type TransportState = {
  vehicules: ListPaginated<VehiculeResource>;
  vehiculeFilter: FilterProps;
  dessertes: ListPaginated<DesserteResource>;
  desserteFilter: FilterProps;
  tournees: ListPaginated<TourneeResource>;
  tourneeFilter: FilterProps;
  factures: ListPaginated<any>;
  factureFilter: FilterProps;
  feries: ListPaginated<any>;
  ferieFilter: FilterProps;
  recus: ListPaginated<RecuResource>;
  recuFilter: FilterProps;
};

const INIT_LIST = {
  data: [],
  total: 0,
  totalPage: 0,
  page: 1,
  totalInDatabase: 0,
};

const INIT_FILTRE = {
  page: 1,
  perpage: 10,
};

const initialState: TransportState = {
  vehicules: { ...INIT_LIST },
  vehiculeFilter: { ...INIT_FILTRE },
  dessertes: { ...INIT_LIST },
  desserteFilter: { ...INIT_FILTRE },
  tournees: { ...INIT_LIST },
  tourneeFilter: { ...INIT_FILTRE },
  factures: { ...INIT_LIST },
  factureFilter: { ...INIT_FILTRE },
  feries: { ...INIT_LIST },
  ferieFilter: { ...INIT_FILTRE },
  recus: { ...INIT_LIST },
  recuFilter: { ...INIT_FILTRE },
};

const transportSlice = createSlice({
  name: "transport",
  initialState,
  reducers: {
    setVehicules(
      state,
      action: PayloadAction<ListPaginated<VehiculeResource>>
    ) {
      state.vehicules = action.payload;
    },
    setVehiculeFilter(state, { payload }: PayloadAction<FilterProps>) {
      state.vehiculeFilter = payload;
      state.vehicules.data = [];
    },
    setDessertes(
      state,
      action: PayloadAction<ListPaginated<DesserteResource>>
    ) {
      state.dessertes = action.payload;
    },
    setDesserteFilter(state, { payload }: PayloadAction<FilterProps>) {
      state.desserteFilter = payload;
      state.dessertes.data = [];
    },
    updateDesserte(state, action: PayloadAction<DesserteItem>) {
      state.dessertes.data = state.dessertes.data.map((item) => {
        if (item.id === action.payload.id) {
          return action.payload;
        }
        return item;
      });
    },

    setTournees(state, action: PayloadAction<ListPaginated<TourneeResource>>) {
      state.tournees = action.payload;
    },
    updateTournee(state, action: PayloadAction<TourneeResource>) {
      state.tournees.data = state.tournees.data.map((item) => {
        if (item.id === action.payload.id) {
          return action.payload;
        }
        return item;
      });
    },
    setTourneeFilter(state, { payload }: PayloadAction<FilterProps>) {
      state.tourneeFilter = payload;
      state.tournees.data = [];
    },

    setTransportFactures(
      state,
      action: PayloadAction<ListPaginated<FactureResource>>
    ) {
      state.factures = action.payload;
    },
    updateTransportFacture(state, action: PayloadAction<FactureResource>) {
      const index = state.factures.data.findIndex(
        ({ id }) => action.payload.id === id
      );
      if (index > -1) {
        state.factures.data[index] = action.payload;
      }
    },
    setTransportFactureFilter(state, { payload }: PayloadAction<FilterProps>) {
      state.factureFilter = payload;
      state.factures.data = [];
    },
    setFeries(state, action: PayloadAction<ListPaginated<FerieResource>>) {
      state.feries = action.payload;
    },
    setFerieFilter(state, { payload }: PayloadAction<FilterProps>) {
      state.ferieFilter = payload;
    },
    setRecus(state, action: PayloadAction<ListPaginated<RecuResource>>) {
      state.recus = action.payload;
    },
    setRecuFilter(state, { payload }: PayloadAction<FilterProps>) {
      state.recuFilter = payload;
      state.recus.data = [];
    },
  },
});

export default transportSlice.reducer;

export const {
  setVehicules,
  setVehiculeFilter,
  setDessertes,
  setDesserteFilter,
  updateDesserte,
  setTournees,
  setTourneeFilter,
  updateTournee,
  setTransportFactures,
  setTransportFactureFilter,
  updateTransportFacture,
  setFeries,
  setFerieFilter,
  setRecuFilter,
  setRecus,
} = transportSlice.actions;

const getTransportReducer = (state: any): TransportState => state.transport;

export const selectVehicules = createSelector(
  getTransportReducer,
  ({ vehicules }) => vehicules
);

export const selectVehiculeFilter = createSelector(
  getTransportReducer,
  ({ vehiculeFilter }) => vehiculeFilter
);

export const selectDessertes = createSelector(
  getTransportReducer,
  ({ dessertes }) => dessertes
);

export const selectDesserteFilter = createSelector(
  getTransportReducer,
  ({ desserteFilter }) => desserteFilter
);

export const selectTournees = createSelector(
  getTransportReducer,
  ({ tournees }) => tournees
);

export const selectTourneeFilter = createSelector(
  getTransportReducer,
  ({ tourneeFilter }) => tourneeFilter
);

export const selectTransportFactures = createSelector(
  getTransportReducer,
  ({ factures }) => factures
);

export const selectTransportFactureFilter = createSelector(
  getTransportReducer,
  ({ factureFilter }) => factureFilter
);

export const selectFerires = createSelector(
  getTransportReducer,
  ({ feries }) => feries
);
export const selectFerieFilter = createSelector(
  getTransportReducer,
  ({ ferieFilter }) => ferieFilter
);

export const selectRecus = createSelector(
  getTransportReducer,
  ({ recus }) => recus
);
export const selectRecuFilter = createSelector(
  getTransportReducer,
  ({ recuFilter }) => recuFilter
);
